import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { IframePidgetProvider, useIframePidget } from "@workspace/utils-react";
import React, { createContext, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import { DiaryContextProvider } from "./context/DiaryContext";
import { UserContextProvider } from "./context/UserContext";
import "./index.css";

export const IframeContext = createContext<boolean>(false);

import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";
import { AppContextProvider } from "./context/AppContext";
import { CollinsUserContextProvider } from "./context/CollinsUserContext";
import { CustomerContextProvider } from "./context/CustomerContext";
import { DeviceContextProvider } from "./context/DeviceContext";
import { FeatureFlagContextProvider } from "./context/FeatureFlagsContext";
import { InfoModalContextProvider } from "./context/InfoModalContext";
import { LoadingStatusProvider } from "./context/LoadingStatusContext";
import { NotificationContextProvider } from "./context/NotificationsContext";
import { UIContextProvider } from "./context/UIContext";
import { dmnIdentity } from "./utils/dmnIdentity";

const useragent = window.navigator.userAgent.toLowerCase();
const isiPad =
  useragent.indexOf("ipad") > -1 ||
  (useragent.indexOf("macintosh") > -1 && "ontouchend" in document);
const usePolyfill = polyfill({
  dragImageTranslateOverride:
    scrollBehaviourDragImageTranslateOverride || isiPad,
  tryFindDraggableTarget: (e: TouchEvent) => {
    /* This is to ensure that the touchevent drag and drop polyfill only actions on items we choose.
         at the moment we bind data-drag="true" to a div. This only works with HTMLElements atm.
         If in future we wish buttons etc to be included amend here */
    const target = e.target as HTMLElement;
    if (target?.dataset?.drag === "true") {
      return target;
    } else {
      return undefined;
    }
  },
  forceApply: isiPad
});
if (usePolyfill) {
  window.addEventListener("touchmove", function () {}, { passive: false });
  window.addEventListener("touchend", function () {}, { passive: false });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

function RootApp() {
  const iframePidget = useIframePidget();
  const isInIframe = !!iframePidget;

  const [hasToken, setHasToken] = React.useState(false);
  useEffect(() => {
    (async () => {
      const token = await dmnIdentity.getAccessToken();
      setHasToken(!!token);
    })();
  }, []);

  if (!hasToken) {
    return null;
  }

  const appContent = (
    <IframeContext.Provider value={isInIframe}>
      <Router>
        <QueryClientProvider client={queryClient}>
          <LoadingStatusProvider>
            <InfoModalContextProvider>
              <DeviceContextProvider>
                <UIContextProvider>
                  <FeatureFlagContextProvider>
                    <NotificationContextProvider>
                      <UserContextProvider>
                        <CollinsUserContextProvider>
                          <DiaryContextProvider>
                            <CustomerContextProvider>
                              <AppContextProvider>
                                <App />
                              </AppContextProvider>
                            </CustomerContextProvider>
                          </DiaryContextProvider>
                        </CollinsUserContextProvider>
                      </UserContextProvider>
                    </NotificationContextProvider>
                  </FeatureFlagContextProvider>
                </UIContextProvider>
              </DeviceContextProvider>
            </InfoModalContextProvider>
          </LoadingStatusProvider>
        </QueryClientProvider>
      </Router>
    </IframeContext.Provider>
  );

  if (iframePidget) {
    return (
      <IframePidgetProvider iframePidget={iframePidget}>
        {appContent}
      </IframePidgetProvider>
    );
  }

  return appContent;
}

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>
);
